import React, { useState, useEffect, useCallback } from 'react'
import { SmartInput } from './SmartInput'
import { MobileInput } from './MobileInput'

// Enhanced validation schema with defensive patterns
const RegistrationSchema = {
  username: (value: string) => {
    if (!value) return { valid: false, message: 'Username is required', code: 'USR_001' }
    if (value.length < 3) return { valid: false, message: 'Username must be at least 3 characters', code: 'USR_002' }
    if (value.length > 20) return { valid: false, message: 'Username cannot exceed 20 characters', code: 'USR_003' }
    if (!/^[A-Za-z0-9_-]+$/.test(value)) return { valid: false, message: 'Username can only contain letters, numbers, underscore, and dash', code: 'USR_004' }
    return { valid: true, message: 'Username is available', code: 'USR_SUCCESS' }
  },
  email: (value: string) => {
    if (!value) return { valid: false, message: 'Email is required', code: 'EML_001' }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) return { valid: false, message: 'Please enter a valid email address', code: 'EML_002' }
    if (value.length > 255) return { valid: false, message: 'Email cannot exceed 255 characters', code: 'EML_003' }
    return { valid: true, message: 'Email is valid', code: 'EML_SUCCESS' }
  },
  password: (value: string) => {
    if (!value) return { valid: false, message: 'Password is required', code: 'PWD_001' }
    if (value.length < 6) return { valid: false, message: 'Password must be at least 6 characters', code: 'PWD_002' }
    if (value.length > 12) return { valid: false, message: 'Password cannot exceed 12 characters', code: 'PWD_003' }
    if (!/^[\x00-\x7F]*$/.test(value)) return { valid: false, message: 'Password must contain only ASCII characters', code: 'PWD_004' }
    if (/\s/.test(value)) return { valid: false, message: 'Password cannot contain spaces', code: 'PWD_005' }
    return { valid: true, message: 'Password meets requirements', code: 'PWD_SUCCESS' }
  },
  mobile: (value: string) => {
    if (!value) return { valid: false, message: 'Mobile number is required', code: 'MOB_001' }
    if (value.length !== 9) return { valid: false, message: 'Mobile number must be exactly 9 digits', code: 'MOB_002' }
    if (!/^\d{9}$/.test(value)) return { valid: false, message: 'Mobile number must contain only digits', code: 'MOB_003' }
    return { valid: true, message: 'Mobile number is valid', code: 'MOB_SUCCESS' }
  }
}

interface FormData {
  username: string
  email: string
  password: string
  confirmPassword: string
  firstName: string
  lastName: string
  mobileNumber: string
  location: string
}

interface ValidationState {
  [key: string]: {
    isValid: boolean
    message: string
    code: string
    isChecking?: boolean
  }
}

interface StatusMessage {
  type: 'error' | 'success' | 'info' | 'warning'
  message: string
  code?: string
  timestamp: number
}

export const EnhancedRegistrationForm: React.FC = () => {
  const [formData, setFormData] = useState<FormData>({
    username: '',
    email: '',
    password: '',
    confirmPassword: '',
    firstName: '',
    lastName: '',
    mobileNumber: '',
    location: ''
  })

  const [validationState, setValidationState] = useState<ValidationState>({})
  const [statusMessages, setStatusMessages] = useState<StatusMessage[]>([])
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [focusedField, setFocusedField] = useState<string | null>(null)
  const [registrationComplete, setRegistrationComplete] = useState(false)

  // Debounced validation function
  const debounce = useCallback((func: Function, wait: number) => {
    let timeout: number
    return (...args: any[]) => {
      clearTimeout(timeout)
      timeout = setTimeout(() => func(...args), wait)
    }
  }, [])

  // Enhanced availability check with server validation
  const checkAvailability = useCallback(
    debounce(async (field: 'username' | 'email', value: string) => {
      if (!value || value.length < 3) return

      setValidationState(prev => ({
        ...prev,
        [field]: { ...prev[field], isChecking: true }
      }))

      try {
        // Use relative URL that works through Traefik proxy
        const response = await fetch(`/check-${field}-availability/${encodeURIComponent(value)}`)
        const data = await response.json()
        
        setValidationState(prev => ({
          ...prev,
          [field]: {
            isValid: data.available,
            message: data.message,
            code: data.code,
            isChecking: false
          }
        }))
      } catch (error) {
        setValidationState(prev => ({
          ...prev,
          [field]: {
            isValid: false,
            message: `Failed to check ${field} availability`,
            code: 'NET_001',
            isChecking: false
          }
        }))
      }
    }, 500),
    []
  )

  // Real-time field validation
  const validateField = useCallback((field: keyof FormData, value: string) => {
    let validation: any

    switch (field) {
      case 'username':
        validation = RegistrationSchema.username(value)
        if (validation.valid && value.length >= 3) {
          checkAvailability('username', value)
        }
        break
      case 'email':
        validation = RegistrationSchema.email(value)
        if (validation.valid) {
          checkAvailability('email', value)
        }
        break
      case 'password':
        validation = RegistrationSchema.password(value)
        break
      case 'confirmPassword':
        validation = {
          valid: value === formData.password,
          message: value === formData.password ? 'Passwords match' : 'Passwords do not match',
          code: value === formData.password ? 'PWD_MATCH' : 'PWD_MISMATCH'
        }
        break
      case 'firstName':
      case 'lastName':
        validation = {
          valid: value.trim().length > 0 && /^[A-Za-z]+$/.test(value),
          message: value.trim().length === 0 ? `${field === 'firstName' ? 'First' : 'Last'} name is required` :
                   /^[A-Za-z]+$/.test(value) ? `${field === 'firstName' ? 'First' : 'Last'} name is valid` :
                   `${field === 'firstName' ? 'First' : 'Last'} name can only contain letters`,
          code: value.trim().length === 0 ? 'NAME_001' : 
                /^[A-Za-z]+$/.test(value) ? 'NAME_SUCCESS' : 'NAME_002'
        }
        break
      case 'mobileNumber':
        validation = RegistrationSchema.mobile(value)
        break
      case 'location':
        validation = {
          valid: value !== '',
          message: value !== '' ? 'Location selected' : 'Location is required',
          code: value !== '' ? 'LOC_SUCCESS' : 'LOC_001'
        }
        break
      default:
        return
    }

    setValidationState(prev => ({
      ...prev,
      [field]: {
        isValid: validation.valid,
        message: validation.message,
        code: validation.code,
        isChecking: false
      }
    }))
  }, [formData.password, checkAvailability])

  // Handle input changes with real-time validation
  const handleInputChange = (field: keyof FormData) => (value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }))
    
    // Clear old status messages for this field
    setStatusMessages(prev => 
      prev.filter(msg => !msg.message.toLowerCase().includes(field.toLowerCase()))
    )
    
    // Validate field in real-time
    validateField(field, value)
  }

  // Add status message with auto-removal
  const addStatusMessage = (type: StatusMessage['type'], message: string, code?: string) => {
    const newMessage: StatusMessage = { 
      type, 
      message, 
      code, 
      timestamp: Date.now() 
    }
    
    setStatusMessages(prev => {
      const filtered = prev.filter(msg => msg.message !== message)
      const updated = [...filtered, newMessage].slice(-3) // Keep last 3 messages
      
      // Auto-remove success messages after 3 seconds
      if (type === 'success') {
        setTimeout(() => {
          setStatusMessages(current => 
            current.filter(msg => msg.timestamp !== newMessage.timestamp)
          )
        }, 3000)
      }
      
      return updated
    })
  }

  // Handle form submission
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    
    // Validate all fields
    Object.keys(formData).forEach(key => {
      validateField(key as keyof FormData, formData[key as keyof FormData])
    })

    // Check if form is valid
    const isFormValid = Object.values(validationState).every(state => 
      state.isValid && !state.isChecking
    )

    if (!isFormValid) {
      addStatusMessage('error', 'Please fix all validation errors before submitting', 'FORM_001')
      return
    }

    setIsSubmitting(true)
    addStatusMessage('info', 'Creating your account...', 'FORM_002')

    try {
      // Use relative URL that works through Traefik proxy
      const response = await fetch('/register-user', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData)
      })

      const data = await response.json()

      if (data.success) {
        // Mark registration as complete to disable all fields
        setRegistrationComplete(true)
        
        // Show success message without redirecting
        addStatusMessage('success', data.message || 'Registration successful. Please Contact Administrator for Approval.', 'REG_001')
        
        // Show final success message
        setTimeout(() => {
          addStatusMessage('info', 'Registration Complete. Please Contact Administrator for Account Approval.', 'REG_002')
        }, 1000)
      } else {
        addStatusMessage('error', data.error || 'Registration failed', data.code)
      }
    } catch (error) {
      addStatusMessage('error', 'Network error. Please try again.', 'NET_002')
    } finally {
      setIsSubmitting(false)
    }
  }

  // Get field validation class
  const getFieldClass = (field: keyof FormData) => {
    const state = validationState[field]
    const isFocused = focusedField === field
    
    let baseClass = 'enhanced-input'
    
    if (isFocused) baseClass += ' focused'
    if (state?.isChecking) baseClass += ' checking'
    if (state && !state.isValid) baseClass += ' error'
    if (state && state.isValid) baseClass += ' success'
    
    return baseClass
  }


  // Handle mouse movement for interactive background
  const handleMouseMove = useCallback((e: React.MouseEvent<HTMLDivElement>) => {
    const container = e.currentTarget
    const rect = container.getBoundingClientRect()
    const x = ((e.clientX - rect.left) / rect.width) * 100
    const y = ((e.clientY - rect.top) / rect.height) * 100
    
    container.style.setProperty('--mouse-x', `${x}%`)
    container.style.setProperty('--mouse-y', `${y}%`)
  }, [])

  return (
    <div 
      className="enhanced-registration-container"
      onMouseMove={handleMouseMove}
    >
      <div className={`registration-panel ${isSubmitting ? 'is-loading' : ''}`}>
        {/* EGL Logo Section */}
        <div className="logo-section">
          <img 
            src="https://verifygold.s3.me-central-1.amazonaws.com/EGL/Logo/EGL_Logo.svg" 
            alt="EGL Logo"
          />
        </div>

        <div className="form-header">
          <h1>Register Account</h1>
        </div>

        <form onSubmit={handleSubmit} className="enhanced-form">
          {/* Username Field */}
          <div className="form-group">
            <div className="input-wrapper">
              <SmartInput
                type="username"
                value={formData.username}
                onChange={handleInputChange('username')}
                maxLength={20}
                placeholder="Username *"
                className={getFieldClass('username')}
                required
                id="username"
                onFocus={() => setFocusedField('username')}
                onBlur={() => setFocusedField(null)}
                disabled={registrationComplete}
                aria-describedby={
                  validationState.username && !validationState.username.isValid ? 'username-error' : undefined
                }
              />
              {validationState.username?.isChecking && (
                <div className="field-status checking">
                  <div className="spinner"></div>
                </div>
              )}
              {validationState.username && !validationState.username.isChecking && (
                <div className={`field-status ${validationState.username.isValid ? 'success' : 'error'}`}>
                  {validationState.username.isValid ? '✓' : '!'}
                </div>
              )}
            </div>
            {validationState.username && !validationState.username.isValid && (
              <div id="username-error" className="field-error">
                <span className="error-message">{validationState.username.message}</span>
                <span className="error-code">{validationState.username.code}</span>
              </div>
            )}
          </div>

          {/* Email Field */}
          <div className="form-group">
            <div className="input-wrapper">
              <SmartInput
                type="email"
                value={formData.email}
                onChange={handleInputChange('email')}
                placeholder="Email *"
                className={getFieldClass('email')}
                required
                id="email"
                onFocus={() => setFocusedField('email')}
                onBlur={() => setFocusedField(null)}
                disabled={registrationComplete}
                aria-describedby={
                  validationState.email && !validationState.email.isValid ? 'email-error' : undefined
                }
              />
              {validationState.email?.isChecking && (
                <div className="field-status checking">
                  <div className="spinner"></div>
                </div>
              )}
              {validationState.email && !validationState.email.isChecking && (
                <div className={`field-status ${validationState.email.isValid ? 'success' : 'error'}`}>
                  {validationState.email.isValid ? '✓' : '!'}
                </div>
              )}
            </div>
            {validationState.email && !validationState.email.isValid && (
              <div id="email-error" className="field-error">
                <span className="error-message">{validationState.email.message}</span>
                <span className="error-code">{validationState.email.code}</span>
              </div>
            )}
          </div>

          {/* Password Fields in Grid */}
          <div className="form-row">
            <div className="form-group">
              <div className="input-wrapper">
                <SmartInput
                  type="text"
                  value={formData.password}
                  onChange={handleInputChange('password')}
                  maxLength={12}
                  placeholder="Password * (6-12 characters)"
                  className={getFieldClass('password')}
                  required
                  id="password"
                  onFocus={() => setFocusedField('password')}
                  onBlur={() => setFocusedField(null)}
                  disabled={registrationComplete}
                  aria-describedby={
                    validationState.password && !validationState.password.isValid ? 'password-error' : undefined
                  }
                />
                {validationState.password && (
                  <div className={`field-status ${validationState.password.isValid ? 'success' : 'error'}`}>
                    {validationState.password.isValid ? '✓' : '!'}
                  </div>
                )}
              </div>
              {validationState.password && !validationState.password.isValid && (
                <div id="password-error" className="field-error">
                  <span className="error-message">{validationState.password.message}</span>
                  <span className="error-code">{validationState.password.code}</span>
                </div>
              )}
            </div>

            <div className="form-group">
              <div className="input-wrapper">
                <SmartInput
                  type="text"
                  value={formData.confirmPassword}
                  onChange={handleInputChange('confirmPassword')}
                  maxLength={12}
                  placeholder="Confirm Password *"
                  className={getFieldClass('confirmPassword')}
                  required
                  id="confirmPassword"
                  onFocus={() => setFocusedField('confirmPassword')}
                  onBlur={() => setFocusedField(null)}
                  disabled={registrationComplete}
                  aria-describedby={
                    validationState.confirmPassword && !validationState.confirmPassword.isValid ? 'confirmPassword-error' : undefined
                  }
                />
                {validationState.confirmPassword && (
                  <div className={`field-status ${validationState.confirmPassword.isValid ? 'success' : 'error'}`}>
                    {validationState.confirmPassword.isValid ? '✓' : '!'}
                  </div>
                )}
              </div>
              {validationState.confirmPassword && !validationState.confirmPassword.isValid && (
                <div id="confirmPassword-error" className="field-error">
                  <span className="error-message">{validationState.confirmPassword.message}</span>
                  <span className="error-code">{validationState.confirmPassword.code}</span>
                </div>
              )}
            </div>
          </div>

          {/* Name Fields in Grid */}
          <div className="form-row">
            <div className="form-group">
              <div className="input-wrapper">
                <SmartInput
                  type="letters-only"
                  value={formData.firstName}
                  onChange={handleInputChange('firstName')}
                  maxLength={50}
                  placeholder="First Name *"
                  className={getFieldClass('firstName')}
                  required
                  id="firstName"
                  onFocus={() => setFocusedField('firstName')}
                  onBlur={() => setFocusedField(null)}
                  disabled={registrationComplete}
                  aria-describedby={
                    validationState.firstName && !validationState.firstName.isValid ? 'firstName-error' : undefined
                  }
                />
                {validationState.firstName && (
                  <div className={`field-status ${validationState.firstName.isValid ? 'success' : 'error'}`}>
                    {validationState.firstName.isValid ? '✓' : '!'}
                  </div>
                )}
              </div>
              {validationState.firstName && !validationState.firstName.isValid && (
                <div id="firstName-error" className="field-error">
                  <span className="error-message">{validationState.firstName.message}</span>
                  <span className="error-code">{validationState.firstName.code}</span>
                </div>
              )}
            </div>

            <div className="form-group">
              <div className="input-wrapper">
                <SmartInput
                  type="letters-only"
                  value={formData.lastName}
                  onChange={handleInputChange('lastName')}
                  maxLength={50}
                  placeholder="Last Name *"
                  className={getFieldClass('lastName')}
                  required
                  id="lastName"
                  onFocus={() => setFocusedField('lastName')}
                  onBlur={() => setFocusedField(null)}
                  disabled={registrationComplete}
                  aria-describedby={
                    validationState.lastName && !validationState.lastName.isValid ? 'lastName-error' : undefined
                  }
                />
                {validationState.lastName && (
                  <div className={`field-status ${validationState.lastName.isValid ? 'success' : 'error'}`}>
                    {validationState.lastName.isValid ? '✓' : '!'}
                  </div>
                )}
              </div>
              {validationState.lastName && !validationState.lastName.isValid && (
                <div id="lastName-error" className="field-error">
                  <span className="error-message">{validationState.lastName.message}</span>
                  <span className="error-code">{validationState.lastName.code}</span>
                </div>
              )}
            </div>
          </div>

          {/* Location and Mobile in Grid */}
          <div className="form-row">
            <div className="form-group">
              <div className="input-wrapper">
                <select
                  id="location"
                  value={formData.location}
                  onChange={(e) => handleInputChange('location')(e.target.value)}
                  className={getFieldClass('location')}
                  required
                  onFocus={() => setFocusedField('location')}
                  onBlur={() => setFocusedField(null)}
                  disabled={registrationComplete}
                  aria-describedby={
                    validationState.location && !validationState.location.isValid ? 'location-error' : undefined
                  }
                >
                  <option value="">Location *</option>
                  <option value="DUBAI">Dubai</option>
                  <option value="DOHA">Doha</option>
                </select>
                {validationState.location && (
                  <div className={`field-status ${validationState.location.isValid ? 'success' : 'error'}`}>
                    {validationState.location.isValid ? '✓' : '!'}
                  </div>
                )}
              </div>
              {validationState.location && !validationState.location.isValid && (
                <div id="location-error" className="field-error">
                  <span className="error-message">{validationState.location.message}</span>
                  <span className="error-code">{validationState.location.code}</span>
                </div>
              )}
            </div>

            <div className="form-group">
              <MobileInput
                selectedLocation={formData.location}
                mobileNumber={formData.mobileNumber}
                onMobileChange={handleInputChange('mobileNumber')}
                required
                id="mobileNumber"
                disabled={registrationComplete}
              />
              {validationState.mobileNumber && !validationState.mobileNumber.isValid && (
                <div id="mobileNumber-error" className="field-error">
                  <span className="error-message">{validationState.mobileNumber.message}</span>
                  <span className="error-code">{validationState.mobileNumber.code}</span>
                </div>
              )}
            </div>
          </div>

          {/* Status Messages */}
          {statusMessages.length > 0 && (
            <div className="status-feedback-box">
              <div
                className="status-messages"
                role="status"
                aria-live="polite"
                aria-atomic="false"
              >
                {statusMessages.map((msg, index) => (
                  <div
                    key={`${msg.timestamp}-${index}`}
                    className={`status-message ${msg.type}`}
                  >
                    <span className="status-text">{msg.message}</span>
                    {msg.code && <span className="status-code">{msg.code}</span>}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Submit Button */}
          <button
            type="submit"
            disabled={registrationComplete || isSubmitting || Object.values(validationState).some(state => 
              !state.isValid || state.isChecking
            )}
            className="enhanced-submit-btn"
          >
            {registrationComplete ? (
              'Registration Complete'
            ) : isSubmitting ? (
              <>
                <div className="spinner"></div>
                Creating Account...
              </>
            ) : (
              'Create Account'
            )}
          </button>
        </form>

        {/* Login Link */}
        <div className="form-footer">
          <p>
            Already have an account?{' '}
            <button 
              type="button"
              onClick={() => { window.history.pushState({}, '', '/login'); window.location.reload(); }}
              className="login-link"
              style={{ background: 'none', border: 'none', cursor: 'pointer' }}
              disabled={registrationComplete || isSubmitting}
            >
              Sign in here
            </button>
          </p>
        </div>
      </div>
    </div>
  )
} 